import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiStar, FiThumbsUp } from 'react-icons/fi';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

const REVIEWS = [
  {
    id: 1,
    author: 'Sarah M.',
    location: 'London, UK',
    rating: 5,
    date: '2024-11-12',
    title: 'Absolutely stunning Ethiopian opal',
    body: 'The play-of-color on this opal is unlike anything I have seen in London jewellery stores. The packaging was exquisite and the certificate of authenticity gave me complete confidence. Will definitely order again.',
    verified: true,
    helpful: 24,
    product: 'Ethiopian Welo Opal Ring',
  },
  {
    id: 2,
    author: 'James K.',
    location: 'Toronto, Canada',
    rating: 5,
    date: '2024-10-28',
    title: 'Perfect engagement ring — she said yes!',
    body: 'Ordered a custom ruby ring for my proposal. The team at Roha was incredibly helpful throughout the process. The ring arrived beautifully packaged and the quality exceeded my expectations.',
    verified: true,
    helpful: 31,
    product: 'Custom Ruby Engagement Ring',
  },
  {
    id: 3,
    author: 'Amina T.',
    location: 'Dubai, UAE',
    rating: 5,
    date: '2024-12-03',
    title: 'Exceptional quality and fast delivery',
    body: 'I was skeptical about ordering fine jewellery online but Roha completely changed my mind. The sapphire necklace is breathtaking and arrived in Dubai within a week, fully insured.',
    verified: true,
    helpful: 18,
    product: 'Blue Sapphire Necklace',
  },
  {
    id: 4,
    author: 'Chen W.',
    location: 'Singapore',
    rating: 4,
    date: '2024-09-15',
    title: 'Beautiful piece, great customer service',
    body: 'The emerald bracelet is gorgeous. Had a small question about sizing and the team responded within hours. The gemstone quality is clearly superior to what I find locally.',
    verified: true,
    helpful: 12,
    product: 'Ethiopian Emerald Bracelet',
  },
  {
    id: 5,
    author: 'Fatima A.',
    location: 'Addis Ababa, Ethiopia',
    rating: 5,
    date: '2025-01-08',
    title: 'Proud to wear Ethiopian heritage',
    body: 'As an Ethiopian, I am so proud to see our gemstones presented with this level of luxury and professionalism. The Habesha gold design is stunning and the craftsmanship is world-class.',
    verified: true,
    helpful: 42,
    product: 'Habesha Gold Necklace',
  },
  {
    id: 6,
    author: 'Marco R.',
    location: 'Milan, Italy',
    rating: 5,
    date: '2024-11-30',
    title: 'Finest opal I have ever purchased',
    body: 'As a jewellery designer, I source gemstones globally. The Ethiopian opals from Roha are among the finest I have encountered. The direct sourcing model ensures exceptional quality at fair prices.',
    verified: true,
    helpful: 29,
    product: 'Premium Welo Opal Collection',
  },
];

const StarRating = ({ rating, size = 16 }) => (
  <div className="star-rating" aria-label={`${rating} out of 5 stars`} role="img">
    {[1, 2, 3, 4, 5].map((star) => (
      <FiStar
        key={star}
        size={size}
        className={star <= rating ? 'star-rating__star star-rating__star--filled' : 'star-rating__star'}
        aria-hidden="true"
      />
    ))}
  </div>
);

const ReviewsSection = ({ compact = false }) => {
  const [helpfulClicked, setHelpfulClicked] = useState({});
  const displayReviews = compact ? REVIEWS.slice(0, 3) : REVIEWS;

  const avgRating = (REVIEWS.reduce((s, r) => s + r.rating, 0) / REVIEWS.length).toFixed(1);
  const totalReviews = REVIEWS.length;

  const handleHelpful = (id) => {
    setHelpfulClicked((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <section className="reviews-section" aria-labelledby="reviews-heading">
      {/* Summary bar */}
      <div className="reviews-summary" role="region" aria-label="Overall rating summary">
        <div className="reviews-summary__score">
          <span className="reviews-summary__avg" aria-label={`Average rating ${avgRating}`}>{avgRating}</span>
          <StarRating rating={Math.round(Number(avgRating))} size={20} />
          <span className="reviews-summary__count">{totalReviews} verified reviews</span>
        </div>
        <div className="reviews-summary__bars" aria-label="Rating distribution">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = REVIEWS.filter((r) => r.rating === star).length;
            const pct = Math.round((count / totalReviews) * 100);
            return (
              <div key={star} className="reviews-summary__bar-row">
                <span className="reviews-summary__bar-label">{star}★</span>
                <div className="reviews-summary__bar-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${star} star: ${pct}%`}>
                  <div className="reviews-summary__bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <span className="reviews-summary__bar-pct">{pct}%</span>
              </div>
            );
          })}
        </div>
        <div className="reviews-summary__badges">
          <span className="reviews-trust-badge">✓ All reviews verified</span>
          <span className="reviews-trust-badge">✓ Genuine customers only</span>
        </div>
      </div>

      {/* Review cards */}
      <motion.div
        className="reviews-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        role="list"
        aria-label="Customer reviews"
      >
        {displayReviews.map((review) => (
          <motion.article
            key={review.id}
            className="review-card"
            variants={fadeUp}
            role="listitem"
            aria-label={`Review by ${review.author}`}
          >
            <header className="review-card__header">
              <div className="review-card__author-info">
                <div className="review-card__avatar" aria-hidden="true">
                  {review.author.charAt(0)}
                </div>
                <div>
                  <p className="review-card__author">{review.author}</p>
                  <p className="review-card__location">{review.location}</p>
                </div>
              </div>
              <div className="review-card__meta">
                <StarRating rating={review.rating} />
                {review.verified && (
                  <span className="review-card__verified" aria-label="Verified purchase">
                    ✓ Verified
                  </span>
                )}
              </div>
            </header>

            <div className="review-card__body">
              <h3 className="review-card__title">"{review.title}"</h3>
              <p className="review-card__text">{review.body}</p>
              <p className="review-card__product">
                <span>Purchased:</span> {review.product}
              </p>
            </div>

            <footer className="review-card__footer">
              <time className="review-card__date" dateTime={review.date}>
                {new Date(review.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </time>
              <button
                className={`review-card__helpful ${helpfulClicked[review.id] ? 'is-clicked' : ''}`}
                onClick={() => handleHelpful(review.id)}
                aria-label={`Mark review by ${review.author} as helpful`}
                aria-pressed={!!helpfulClicked[review.id]}
              >
                <FiThumbsUp size={13} aria-hidden="true" />
                Helpful ({review.helpful + (helpfulClicked[review.id] ? 1 : 0)})
              </button>
            </footer>
          </motion.article>
        ))}
      </motion.div>
    </section>
  );
};

export default ReviewsSection;
